import {
  Box,
  ClickAwayListener,
  Drawer,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { productCondition, unitCode } from "../../../common/models/CommonModels";
import ActionToast from "../../../shared/components/ActionToast";
import StockItemTableAvailability from "../../../customer/inventory/components/StockItemTableAvailability";
import ImageBox from "../../../shared/components/ImageBox";
import Table from "../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import TablePagination from "../../../shared/components/tables/TablePagination";
import Loader from "../../../shared/components/Loader";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import AdminSearch from "../AdminSearch";
import { changeQuery, getStockItems, selectStockItem } from "../../adminStockItemsSlice";
import { AdminInventoryState, AdminStockItem, AdminStockItemsQueryParameter } from "../../models/AdminStockItemsModels";
import InventoryDetails from "./InventoryDetails";

const Inventory = () => {
  const { query, stockItems, stockItem, loading, success, error } = useAppSelector<AdminInventoryState>(
    (state: any) => state.adminInventory
  );

  const dispatch = useAppDispatch();
  const { translate: tCommon } = useTranslation("common");
  const { translate: tInventory } = useTranslation("inventory");

  const [productDetails, setProductDetails] = useState(false);

  const columns: TableColumnDefinition[] = [
    { title: tInventory("product"), parameter: AdminStockItemsQueryParameter.Name },
    { title: tCommon("brand"), parameter: AdminStockItemsQueryParameter.ManufacturerName },
    { title: tCommon("condition") },
    { title: tInventory("availability") },
    { title: tCommon("priceEuro"), parameter: AdminStockItemsQueryParameter.Price },
    { title: tInventory("quantity") },
    { title: tInventory("company", { defaultValue: "Company" }), parameter: AdminStockItemsQueryParameter.SupplierName },
  ];

  const handleSearch = (value?: string) => {
    const newQuery = { ...query, search: value, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handleStockItemSelect = (item: AdminStockItem) => {
    dispatch(selectStockItem(item));
    setProductDetails(true);
  };

  const handleClickAway = () => {
    dispatch(selectStockItem(null));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeQuery(newQuery));
    dispatch(getStockItems(newQuery));
  };

  useEffect(() => {
    dispatch(getStockItems(query));
  }, []);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  if (loading) {
    return <Loader />;
  }
  return (
    <ThemeProvider theme={theme}>
      <ActionToast open={!!success} variant="success" message={success || ""} onClose={() => { }} duration={6000} />

      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">{tInventory("myWarehouse")}</p>
          <div className="ml-7">
            <AdminSearch value={query.search} onSearch={handleSearch} />
          </div>
        </div>
      </Toolbar>

      <Table className="px-3 v-fill">
        <SortTableHead columns={columns} sortBy={query.sortBy} sortDesc={query.sortDesc} onSort={handleSort} />

        <TableBody>
          {stockItems?.items.map((stockItem) => (
            <TableRow
              key={stockItem.id}
              onClick={(event) => {
                handleStockItemSelect(stockItem);
                event.stopPropagation();
              }}
            >
              <TableCell>
                <div className="flex items-center">
                  <ImageBox size="small" src={stockItem.file?.content} />
                  <Box className="ms-3" >
                    <Box className="text-14-medium" color={"#333333"}>{stockItem.product?.number}</Box>
                    <Box className="text-12-medium" lineHeight={"17px"}>{stockItem.alternativeNumber}</Box>
                  </Box>
                </div>
              </TableCell>

              <TableCell>
                <Box className="text-14-medium uppercase" color={"#333333"}>
                  {stockItem.product?.manufacturer?.name}
                </Box>
                <Box className="text-14-regular" color={"#747881"}>
                  {stockItem.product?.category?.name}
                </Box>
              </TableCell>

              <TableCell>
                <Box className="text-14-medium" color={"#333333"}>
                  {tCommon(`productCondition.${productCondition(stockItem.conditionCode)}`)}
                </Box>
              </TableCell>

              <TableCell>
                <StockItemTableAvailability availabilityCode={stockItem.availabilityCode} />
              </TableCell>

              <TableCell>
                <NumericFormat value={stockItem.price?.value} displayType="text" thousandSeparator=" " suffix="€" />
              </TableCell>

              <TableCell>
                {stockItem.quantity?.value} {tCommon(`measurementUnits.${unitCode(stockItem.quantity?.unitCode)}`)}
              </TableCell>

              <TableCell>{stockItem.supplier.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        variant="bottom"
        pageNo={query.pageNo}
        pageSize={query.pageSize}
        totalCount={stockItems?.totalCount}
        onPageNoChange={handlePageNoChange}
        onPageSizeChange={handlePageSizeChange} />

      {stockItem && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div className="absolute">
            <Drawer
              variant="persistent"
              anchor="right"
              open={productDetails}
              onClose={() => setProductDetails(false)}
              hideBackdrop
              elevation={0}
              PaperProps={{
                sx: {
                  background: "#FFF !important",
                  borderLeft: "1px solid #D5D6D9",
                  marginTop: "75px",
                  height: "calc(100% - 75px)"
                },
              }}
            >
              <InventoryDetails />
            </Drawer>
          </div>
        </ClickAwayListener>
      )}
    </ThemeProvider>
  );
};
export default Inventory;
